import { useState } from 'react'
import { Box, Button, Field, Flex, HStack, Input, Stack, Switch, Text } from '@chakra-ui/react'
import { EventVideoAI } from '../EventVideoAI'
import { LinkWithQr } from '../LinkWithQr'
import { toaster } from '../ui/toaster'
import { backfillEventStream, backfillGalleryAuthors, fetchVideos } from '../../services/firebaseService'
import { participantNames } from '../../utils/participantNames'
import { galleryUrl } from '../../utils/webappUrl'
import { useTranslation } from '../../i18n'

function SettingSwitch({ checked, onChange, disabled, label, help }) {
  return (
    <Flex align="center" gap={3} wrap="wrap">
      <Switch.Root checked={checked} onCheckedChange={(d) => onChange(d.checked)} disabled={disabled}>
        <Switch.HiddenInput />
        <Switch.Control><Switch.Thumb /></Switch.Control>
        <Switch.Label>{label}</Switch.Label>
      </Switch.Root>
      {help && <Text fontSize="sm" color="fg.muted">{help}</Text>}
    </Flex>
  )
}

export function RecordingSection({ s }) {
  const { t } = useTranslation()
  return (
    <Flex gap={6} wrap="wrap">
      <Field.Root flex="1" minW="240px">
        <Field.Label>{t('events.maxSegmentDuration')}</Field.Label>
        <Text fontSize="xs" color="fg.muted">{t('events.maxSegmentDurationHelp')}</Text>
        <Input
          type="number"
          min={1}
          max={300}
          value={s.draft?.maxSegmentDuration ?? 5}
          onChange={(e) => s.setField('maxSegmentDuration', parseFloat(e.target.value) || 5)}
          disabled={!s.editing}
          w={{ base: '100%', sm: '100px' }}
        />
      </Field.Root>
      <Field.Root flex="1" minW="240px">
        <Field.Label>{t('events.maxRecordingDuration')}</Field.Label>
        <Text fontSize="xs" color="fg.muted">{t('events.maxRecordingDurationHelp')}</Text>
        <Input
          type="number"
          min={1}
          max={600}
          value={s.draft?.maxRecordingDuration ?? 15}
          onChange={(e) => s.setField('maxRecordingDuration', parseFloat(e.target.value) || 15)}
          disabled={!s.editing}
          w={{ base: '100%', sm: '100px' }}
        />
      </Field.Root>
    </Flex>
  )
}

export function ReceptionSection({ s }) {
  const { t } = useTranslation()
  return (
    <Stack gap={5}>
      <SettingSwitch
        checked={s.draft?.receptionEnabled ?? true}
        onChange={(v) => s.setField('receptionEnabled', v)}
        disabled={!s.editing}
        label={t('events.acceptSubmissions')}
        help={t('events.acceptSubmissionsHelp')}
      />
      {s.draft?.receptionEnabled !== false && (
        <Flex gap={4} wrap="wrap">
          <Field.Root flex="1" minW="220px">
            <Field.Label>{t('events.receptionStart')}</Field.Label>
            <Input
              type="datetime-local"
              value={s.draft?.receptionStart || ''}
              onChange={(e) => s.setField('receptionStart', e.target.value)}
              disabled={!s.editing}
            />
          </Field.Root>
          <Field.Root flex="1" minW="220px">
            <Field.Label>{t('events.receptionEnd')}</Field.Label>
            <Input
              type="datetime-local"
              value={s.draft?.receptionEnd || ''}
              onChange={(e) => s.setField('receptionEnd', e.target.value)}
              disabled={!s.editing}
            />
          </Field.Root>
        </Flex>
      )}
    </Stack>
  )
}

export function ModerationSection({ s }) {
  const { t } = useTranslation()
  return (
    <SettingSwitch
      checked={s.draft?.defaultModerated ?? false}
      onChange={(v) => s.setField('defaultModerated', v)}
      disabled={!s.editing}
      label={t('events.autoApprove')}
      help={t('events.autoApproveHelp')}
    />
  )
}

// Everyone who shared an accepted video, by the name they typed on entry —
// for the couple's thank-you credits. Fetched only on request: an event's
// videos are a full collection read, not something to pay for on every visit.
export function GuestsSection({ s }) {
  const { t } = useTranslation()
  const [generating, setGenerating] = useState(false)
  const [list, setList] = useState(null)

  const generate = async () => {
    setGenerating(true)
    try {
      setList(participantNames(await fetchVideos(s.eventId, 'accepted')))
    } catch (err) {
      toaster.create({ title: err.message, status: 'error' })
    } finally {
      setGenerating(false)
    }
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(list.names.join('\n'))
      toaster.create({ title: t('guests.copied'), status: 'success' })
    } catch (err) {
      toaster.create({ title: err.message, status: 'error' })
    }
  }

  return (
    <Stack gap={4} align="flex-start">
      <Button colorPalette="brand" onClick={generate} loading={generating} loadingText={t('guests.generating')}>
        {list ? t('guests.regenerate') : t('guests.generate')}
      </Button>

      {list && (
        <Stack gap={3} w="100%">
          <HStack justify="space-between" wrap="wrap">
            <Text fontWeight="semibold">{t('guests.count', { count: list.names.length })}</Text>
            {list.names.length > 0 && (
              <Button size="sm" variant="outline" onClick={copy}>{t('guests.copy')}</Button>
            )}
          </HStack>
          {list.names.length === 0
            ? <Text fontSize="sm" color="fg.muted">{t('guests.empty')}</Text>
            : (
              <Box as="ol" listStyleType="none" borderWidth="1px" borderRadius="md" p={3} maxH="420px" overflowY="auto">
                {list.names.map((name) => <Box as="li" key={name} py={0.5}>{name}</Box>)}
              </Box>
            )}
          {list.unnamed > 0 && (
            <Text fontSize="xs" color="fg.muted">{t('guests.unnamed', { count: list.unnamed })}</Text>
          )}
        </Stack>
      )}
    </Stack>
  )
}

// Video AI keeps its own settings document and its own Save, so this tab has
// no Edit button of its own.
export function VideoAISection({ s }) {
  return <EventVideoAI eventId={s.eventId} />
}

export function StreamingSection({ s }) {
  const { t } = useTranslation()
  const [refreshingAuthors, setRefreshingAuthors] = useState(false)
  const streaming = !!s.draft?.streamEnabled
  return (
    <Stack gap={4}>
      <Switch.Root
        checked={streaming}
        disabled={!s.isSysAdmin || !s.editing}
        onCheckedChange={(d) => s.setField('streamEnabled', d.checked)}
      >
        <Switch.HiddenInput />
        <Switch.Control><Switch.Thumb /></Switch.Control>
        <Switch.Label>{t('events.enableStreaming')}</Switch.Label>
      </Switch.Root>
      {!s.isSysAdmin && <Text fontSize="xs" color="fg.muted">{t('events.streamingSysadminOnly')}</Text>}

      {streaming && (
        <>
          <Switch.Root
            checked={!!s.draft?.galleryPublic}
            disabled={!s.isSysAdmin || !s.editing}
            onCheckedChange={(d) => s.setField('galleryPublic', d.checked)}
          >
            <Switch.HiddenInput />
            <Switch.Control><Switch.Thumb /></Switch.Control>
            <Switch.Label>{t('events.galleryPublic')}</Switch.Label>
          </Switch.Root>
          <Text fontSize="xs" color="fg.muted">{t('events.galleryPublicHint')}</Text>
        </>
      )}

      {s.eventId && streaming && (
        <Box>
          <Text fontSize="sm" mb={1}>{t('events.galleryUrl')}</Text>
          <LinkWithQr url={galleryUrl(s.eventId)} qrTitle={t('events.galleryQrTitle')} downloadName={`gallery-qr-${s.eventId}`} />
        </Box>
      )}

      {s.event?.streamBackfillPending && (
        <HStack wrap="wrap">
          <Text fontSize="xs" color="yellow.fg">{t('events.backfillPending')}</Text>
          <Button
            size="xs"
            variant="outline"
            onClick={async () => {
              try {
                const res = await backfillEventStream(s.eventId)
                toaster.create({ title: t('events.sendRemainingDone', { count: res.sent, remaining: res.remaining }), status: 'success' })
              } catch (err) {
                toaster.create({ title: err.message, status: 'error' })
              }
            }}
          >
            {t('events.sendRemaining')}
          </Button>
        </HStack>
      )}

      {s.eventId && streaming && (
        <Box>
          <Button
            size="xs"
            variant="outline"
            loading={refreshingAuthors}
            onClick={async () => {
              setRefreshingAuthors(true)
              try {
                const res = await backfillGalleryAuthors(s.eventId)
                toaster.create({ title: t('events.backfillAuthorsDone', { updated: res.updated, scanned: res.scanned, recovered: res.recovered }), status: 'success' })
              } catch (err) {
                toaster.create({ title: err.message, status: 'error' })
              } finally {
                setRefreshingAuthors(false)
              }
            }}
          >
            {t('events.backfillAuthors')}
          </Button>
          <Text fontSize="xs" color="fg.muted" mt={1}>{t('events.backfillAuthorsHint')}</Text>
        </Box>
      )}
    </Stack>
  )
}
